// src/App.js
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useUser, API_BASE } from "./context/UserContext";
import MapView from "./components/MapView";
import Sidebar from "./components/Sidebar";
import RestaurantPanel from "./components/RestaurantPanel";
import AuthScreen from "./components/AuthScreen";
import BottomTabBar from "./components/BottomTabBar";
import ProfilePage from "./components/ProfilePage";
import CurationTab from "./components/CurationTab";
import SocialTab from "./components/SocialTab";
import LocationButton from "./components/LocationButton";
import MapFilter from "./components/MapFilter";
import RefreshButton from "./components/RefreshButton";
import PublicProfile from "./components/PublicProfile";
import UserProfileView from "./components/UserProfileView";
import PublicListPage from "./components/PublicListPage";
import PublicBestPicksPage from "./components/PublicBestPicksPage";
import PublicTripPage from "./components/PublicTripPage";
import TripTimeline from "./components/TripTimeline";
import TripPlayback from "./components/TripPlayback";
import InteractiveTutorial from "./components/InteractiveTutorial";
import LoginPrompt from "./components/LoginPrompt";
import Toast from "./components/Toast";
import FeedbackButton from "./components/FeedbackButton";
import SharedPlacesList from "./components/SharedPlacesList";
import PersonalPlacesList from "./components/PersonalPlacesList";
import SavePlaceModal from "./components/SavePlaceModal";
import { FOLLOWING_COLORS, BEST_CATEGORIES, SHARED_CAT_COLOR } from "./constants";
import { useTranslation } from "react-i18next";
import "./App.css";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";

// ── Layout constants ─────────────────────────────────────────
const SIDEBAR_W = 300;
const PANEL_W   = 325;

export default function App() {
  const { user, loading } = useUser();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("map");
  const [unreadCount, setUnreadCount] = useState(0);
  const mapRef = useRef(null);
  const pendingDeepLink = useRef(null);
  const pullStartY = useRef(null);
  const [pullRefreshing, setPullRefreshing] = useState(false);

  const [tutorialStep, setTutorialStep] = useState(null); // null = off, 0-11 = active step
  const [sharedListMode, setSharedListMode] = useState("peek");
  const [previewPlace, setPreviewPlace] = useState(null);
  const [previewSaveOpen, setPreviewSaveOpen] = useState(false);
  const previewMarkerRef = useRef(null);
  const prevUserRef = useRef(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [accounts] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [activeTrip, setActiveTrip] = useState(null);
  const [tripPlayback, setTripPlayback] = useState(false);
  const prevTabBeforeTrip = useRef(null);
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [personalPlaces, setPersonalPlaces] = useState([]);
  const [showPersonal, setShowPersonal] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedFollowingIds, setSelectedFollowingIds] = useState([]);
  const [followingPlacesMap, setFollowingPlacesMap] = useState({});
  const [followingList, setFollowingList] = useState([]);
  const [folders, setFolders] = useState([]);
  const [viewingUserNickname, setViewingUserNickname] = useState(null);
  const [toast, setToast] = useState(null);

  // ── Guest places (localStorage) ────────────────────────────
  const GUEST_KEY = "globalplace_guest_places";
  const [guestPlaces, setGuestPlaces] = useState(() => {
    try { return JSON.parse(localStorage.getItem(GUEST_KEY) || "[]"); } catch { return []; }
  });
  const saveGuestPlaces = useCallback((places) => {
    setGuestPlaces(places);
    localStorage.setItem(GUEST_KEY, JSON.stringify(places));
  }, []);
  const addGuestPlace = useCallback((place) => {
    const gp = { ...place, id: `guest_${Date.now()}`, _guest: true };
    saveGuestPlaces([...guestPlaces, gp]);
    return gp;
  }, [guestPlaces, saveGuestPlaces]);
  const clearGuestPlaces = useCallback(() => {
    setGuestPlaces([]);
    localStorage.removeItem(GUEST_KEY);
  }, []);

  // ── Navigation stack (back) ─────────────────────────────
  const navStackRef = useRef([]);
  const [navDepth, setNavDepth] = useState(0);
  const pushNav = useCallback((snapshot) => {
    navStackRef.current.push(snapshot);
    setNavDepth(navStackRef.current.length);
    window.history.pushState({ navDepth: navStackRef.current.length }, "");
  }, []);
  const goBack = useCallback(() => {
    const stack = navStackRef.current;
    if (stack.length === 0) return false;
    const prev = stack.pop();
    setNavDepth(stack.length);
    if (prev.activeTab != null) setActiveTab(prev.activeTab);
    if (prev.selectedRestaurant !== undefined) setSelectedRestaurant(prev.selectedRestaurant);
    if (prev.viewingUserNickname !== undefined) setViewingUserNickname(prev.viewingUserNickname);
    if (prev.extra) prev.extra();
    return true;
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (navStackRef.current.length > 0) {
        goBack();
      }
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [goBack]);

  // Lock to portrait on mobile
  useEffect(() => {
    try {
      screen.orientation?.lock?.("portrait").catch(() => {});
    } catch {}
  }, []);

  // ── Shared space ────────────────────────────────────────────
  const [mapMode, setMapMode] = useState(() => localStorage.getItem("mapMode") || "shared"); // "personal" | "shared"
  const [sharedCategory, setSharedCategory] = useState(null); // null = all
  const [sharedPlaces, setSharedPlaces] = useState([]);
  const [myBestPicks, setMyBestPicks] = useState({});

  const isMobile = window.innerWidth <= 768;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const showMap = activeTab === "map";

  // Map centering helper — accounts for UI panel offset
  const centerMapOnPlace = useCallback((lat, lng, { noOffset, tripMode } = {}) => {
    if (!mapRef.current || !window.google?.maps) return;
    const mobile = window.innerWidth <= 768;
    let cLat = Number(lat), cLng = Number(lng);
    if (tripMode && mobile) {
      // Timeline takes ~120px at bottom; shift center up so marker is in visible area
      const timelineH = 120;
      const visibleH = window.innerHeight - timelineH;
      const offsetPx = (window.innerHeight / 2) - (visibleH / 2);
      const metersPerPx = 156543.03392 / Math.pow(2, 16);
      cLat -= (offsetPx * metersPerPx) / 111320;
    } else if (!noOffset && !tripMode) {
      if (mobile) cLat -= 0.0023;
      else cLng -= 0.004;
    }
    mapRef.current.setZoom(16);
    mapRef.current.setCenter({ lat: cLat, lng: cLng });
  }, []);

  // ── Data loading ────────────────────────────────────────────
  const loadPersonalPlaces = useCallback(() => {
    if (!user) return Promise.resolve();
    return axios.get(`${API_BASE}/personal-places/`)
      .then((res) => {
        setPersonalPlaces(res.data);
        // Onboarding is now triggered on login only
      }).catch(() => {});
  }, [user]);

  const loadFollowingList = useCallback(() => {
    if (!user) return Promise.resolve();
    return axios.get(`${API_BASE}/follows/me/following`)
      .then((res) => setFollowingList(res.data)).catch(() => {});
  }, [user]);

  const loadUnread = useCallback(() => {
    if (!user) return;
    axios.get(`${API_BASE}/notifications/`)
      .then((res) => setUnreadCount(res.data.filter((n) => !n.is_read).length))
      .catch(() => {});
  }, [user]);

  const loadFolders = useCallback(() => {
    if (!user) return Promise.resolve();
    return axios.get(`${API_BASE}/folders/`)
      .then((res) => setFolders(res.data)).catch(() => {});
  }, [user]);

  const loadSharedPlaces = useCallback((cat = null) => {
    const url = cat ? `${API_BASE}/shared-map/?category=${cat}` : `${API_BASE}/shared-map/`;
    return axios.get(url).then((res) => setSharedPlaces(res.data)).catch(() => {});
  }, []);

  const loadMyBestPicks = useCallback(() => {
    if (!user) return Promise.resolve();
    return axios.get(`${API_BASE}/shared-map/my-picks`)
      .then((res) => setMyBestPicks(res.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user) {
      // Migrate guest places to server on login
      const pending = JSON.parse(localStorage.getItem(GUEST_KEY) || "[]");
      if (pending.length > 0) {
        const migrate = async () => {
          let migrated = 0;
          for (const gp of pending) {
            try {
              const { id, _guest, ...payload } = gp;
              // Remove blob URLs (they won't work on server)
              if (payload.photo_url?.startsWith("blob:")) payload.photo_url = null;
              if (payload.photo_urls) payload.photo_urls = payload.photo_urls.filter((u) => !u.startsWith("blob:")) || null;
              if (payload.photo_urls?.length === 0) payload.photo_urls = null;
              await axios.post(`${API_BASE}/personal-places/`, payload);
              migrated++;
            } catch {}
          }
          clearGuestPlaces();
          if (migrated > 0) {
            loadPersonalPlaces();
            setToast(`${migrated} place${migrated > 1 ? "s" : ""} saved to your account!`);
          }
        };
        migrate();
      }

      loadPersonalPlaces(); loadFollowingList(); loadUnread(); loadFolders();
      loadMyBestPicks();
      // Show onboarding only on fresh login (not page reload)
      if (!prevUserRef.current && !localStorage.getItem("onboarding_dismissed")) {
        setTutorialStep(0);
      }
      setShowLoginPrompt(false);
    }
    prevUserRef.current = user;
  }, [user, loadPersonalPlaces, loadFollowingList, loadUnread, loadFolders, loadMyBestPicks, clearGuestPlaces]); // eslint-disable-line

  // ── Tutorial step advancement on user actions ──────────────────
  const tutorialStepRef = useRef(tutorialStep);
  tutorialStepRef.current = tutorialStep;

  // Auto-navigate when certain steps begin
  useEffect(() => {
    // Step 5 (saved info) → stay on search
    // Step 6 (people tab) → ensure on search tab
    if (tutorialStep === 5 || tutorialStep === 6) {
      if (activeTab !== "curation") setActiveTab("curation");
    }
    // Step 9 (Our Space toggle) → ensure on map tab
    if (tutorialStep === 9 && activeTab !== "map") {
      setActiveTab("map");
    }
  }, [tutorialStep]); // eslint-disable-line

  useEffect(() => {
    const s = tutorialStepRef.current;
    // Step 1: user taps search tab
    if (s === 1 && activeTab === "curation") setTutorialStep(2);
    // Step 8: user taps home (map) tab
    if (s === 8 && activeTab === "map") setTutorialStep(9);
    // Step 10: user taps profile tab
    if (s === 10 && activeTab === "profile") setTutorialStep(11);
  }, [activeTab]);

  useEffect(() => {
    // Step 9: user taps "Our Space"
    if (tutorialStepRef.current === 9 && mapMode === "shared") setTutorialStep(10);
  }, [mapMode]);

  // Load data on shared mode entry
  useEffect(() => {
    if (mapMode === "shared") loadSharedPlaces(sharedCategory);
  }, [mapMode, sharedCategory, loadSharedPlaces]);

  // ── Deep link handling (?place=ID) ───────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const placeId = params.get("place");
    if (!placeId) return;
    window.history.replaceState({}, "", window.location.pathname);
    axios.get(`${API_BASE}/personal-places/${placeId}/detail`)
      .then((res) => {
        const found = res.data;
        setSelectedRestaurant({ ...found, sources: [], isPersonal: true });
        setActiveTab("map");
        // Pan to place if map is ready, otherwise save for later
        if (mapRef.current && window.google?.maps) {
          centerMapOnPlace(found.lat, found.lng);
        } else {
          pendingDeepLink.current = found;
        }
      }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => { loadUnread(); }, 30000);
    return () => clearInterval(interval);
  }, [user, loadUnread]);

  // ── Guest: warn before leaving if they have unsaved data ───
  useEffect(() => {
    if (user || guestPlaces.length === 0) return;
    const handler = (e) => { e.preventDefault(); };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [user, guestPlaces.length]);

  const handleShareTrip = useCallback(async () => {
    if (!activeTrip) return;
    const url = `${window.location.origin}/trip/${activeTrip.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast("Link copied!");
    } catch {
      window.prompt("Copy this link:", url);
    }
  }, [activeTrip]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      loadPersonalPlaces(), loadFollowingList(), loadUnread(),
      ...selectedFollowingIds.map((uid) =>
        axios.get(`${API_BASE}/users/${uid}/places`)
          .then((res) => setFollowingPlacesMap((m) => ({ ...m, [uid]: res.data.filter((p) => p.is_public !== false) })))
          .catch(() => {})
      ),
    ]);
  }, [loadPersonalPlaces, loadFollowingList, loadUnread, selectedFollowingIds]);

  const handleToggleFollowing = useCallback((targetUserId) => {
    setSelectedFollowingIds((prev) => {
      if (prev.includes(targetUserId)) return prev.filter((id) => id !== targetUserId);
      return [...prev, targetUserId];
    });
    // Load places data asynchronously if not available
    if (!followingPlacesMap[targetUserId]) {
      axios.get(`${API_BASE}/users/${targetUserId}/places`)
        .then((res) => setFollowingPlacesMap((m) => ({ ...m, [targetUserId]: res.data.filter((p) => p.is_public !== false) })))
        .catch(() => setFollowingPlacesMap((m) => ({ ...m, [targetUserId]: [] })));
    }
  }, [followingPlacesMap]);

  const followingPlaces = selectedFollowingIds.map((uid) => {
    const u = followingList.find((f) => f.id === uid);
    return { userId: uid, nickname: u?.nickname || "?", colorIdx: followingList.findIndex((f) => f.id === uid), places: followingPlacesMap[uid] || [] };
  });

  const handleMarkerClick = useCallback(async (restaurantId, isPersonal = false) => {
    if (isPersonal) {
      const place = personalPlaces.find((p) => `personal_${p.id}` === restaurantId);
      if (place) setSelectedRestaurant({ ...place, sources: [], isPersonal: true });
      return;
    }
    const res = await axios.get(`${API_BASE}/restaurants/${restaurantId}`);
    setSelectedRestaurant(res.data);
  }, [personalPlaces]);

  const handleFollowingMarkerClick = useCallback((place) => {
    setSelectedRestaurant({ ...place, sources: [], isPersonal: true });
  }, []);

  const handleViewUserProfile = useCallback((nickname) => {
    pushNav({ viewingUserNickname: null });
    setViewingUserNickname(nickname);
  }, [pushNav]);

  // View another user's map — toggle markers on main map
  const handleViewUserMap = useCallback((userId) => {
    // Activate following markers (add if not already active)
    setSelectedFollowingIds((prev) =>
      prev.includes(userId) ? prev : [...prev, userId]
    );

    // Fit map bounds after loading places
    const fitBounds = (places) => {
      if (!mapRef.current || !window.google?.maps || !places.length) return;
      if (places.length === 1) {
        centerMapOnPlace(places[0].lat, places[0].lng);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        places.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
        mapRef.current.fitBounds(bounds, 60);
      }
    };

    if (followingPlacesMap[userId]) {
      setTimeout(() => fitBounds(followingPlacesMap[userId]), 100);
    } else {
      axios.get(`${API_BASE}/users/${userId}/places`)
        .then((res) => {
          const places = res.data.filter((p) => p.is_public !== false);
          setFollowingPlacesMap((m) => ({ ...m, [userId]: places }));
          setTimeout(() => fitBounds(places), 100);
        })
        .catch(() => setFollowingPlacesMap((m) => ({ ...m, [userId]: [] })));
    }

    // Close profile and navigate to map
    setViewingUserNickname(null);
    setActiveTab("map");
    setMapMode("personal");
    // Clear nav stack + browser history
    const depth = navStackRef.current.length;
    navStackRef.current = [];
    setNavDepth(0);
    if (depth > 0) window.history.go(-depth);
  }, [followingPlacesMap, centerMapOnPlace]);

  const handleActivityPlaceClick = useCallback((activity) => {
    pushNav({ activeTab: activeTabRef.current, selectedRestaurant: null });
    // Own places pass _original with full data; following items have activity shape
    if (activity._original) {
      setSelectedRestaurant({ ...activity._original, sources: [], isPersonal: true });
    } else {
      setSelectedRestaurant({
        id: activity.place_id, name: activity.place_name,
        address: activity.place_address,
        lat: activity.place_lat, lng: activity.place_lng,
        category: activity.place_category,
        status: activity.place_status,
        rating: activity.rating,
        memo: activity.memo,
        photo_url: activity.photo_url,
        photo_urls: activity.photo_urls || [],
        instagram_post_url: activity.instagram_post_url,
        like_count: activity.like_count,
        comment_count: activity.comment_count,
        user_id: activity.owner_id,
        owner_nickname: activity.owner_nickname,
        isPersonal: true, sources: [],
      });
    }
    setActiveTab("map");
    const lat = activity.lat || activity.place_lat;
    const lng = activity.lng || activity.place_lng;
    if (lat && lng) centerMapOnPlace(lat, lng);
  }, [centerMapOnPlace, pushNav]);

  const handleNotificationPlaceClick = useCallback((placeId) => {
    pushNav({ activeTab: activeTabRef.current, selectedRestaurant: null });
    axios.get(`${API_BASE}/personal-places/${placeId}/detail`)
      .then((res) => {
        const p = res.data;
        setSelectedRestaurant({ ...p, sources: [], isPersonal: true });
        setActiveTab("map");
        if (p.lat && p.lng) centerMapOnPlace(p.lat, p.lng);
      })
      .catch(() => {});
  }, [centerMapOnPlace, pushNav]);

  const hideRestaurant = useCallback((restaurantId, isPersonal = false) => {
    if (isPersonal) {
      const place = personalPlaces.find((p) => `personal_${p.id}` === restaurantId || p.id === restaurantId);
      if (place) axios.delete(`${API_BASE}/personal-places/${place.id}`)
        .then(() => setPersonalPlaces((prev) => prev.filter((p) => p.id !== place.id)));
    } else {
      setHiddenIds((prev) => new Set([...prev, restaurantId]));
    }
    setSelectedRestaurant(null);
  }, [personalPlaces, user]);

  const addPersonalPlace = useCallback(async (place) => {
    try {
      const payload = { ...place, folder_id: place.folder_id || null, status: place.status || "want_to_go", rating: place.rating || null, memo: place.memo || null, photo_url: place.photo_url || null, photo_urls: place.photo_urls || null, instagram_post_url: place.instagram_post_url || null };
      const url = `${API_BASE}/personal-places/`;
      const res = await axios.post(url, payload);
      setPersonalPlaces((prev) => { const e = prev.find((p) => p.id === res.data.id); return e ? prev : [...prev, res.data]; });
    } catch (e) { console.error("Save failed", e); }
  }, [user]);

  const deletePersonalPlace = useCallback(async (placeId) => {
    await axios.delete(`${API_BASE}/personal-places/${placeId}`);
    setPersonalPlaces((prev) => prev.filter((p) => p.id !== placeId));
    setToast("Removed");
  }, [user]);

  const handlePlaceUpdated = useCallback((updated) => {
    setPersonalPlaces((prev) => prev.map((p) => p.id === updated.id ? { ...p, ...updated } : p));
  }, []);

  const addPlace = (p) => { if (!p) return; setPersonalPlaces((prev) => { const e = prev.find((x) => x.id === p.id); return e ? prev : [...prev, p]; }); setToast("Saved"); };

  const handleBestPickAdd = useCallback(async (data) => {
    if (!user) return;
    try {
      await axios.post(`${API_BASE}/shared-map/my-picks`, data);
      await Promise.all([loadMyBestPicks(), loadSharedPlaces(sharedCategory)]);
      setToast("Added to best picks");
      return { ok: true };
    } catch (e) {
      if (e.response?.status === 409) return { ok: false, detail: e.response.data.detail };
      setToast(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed to add");
      return { ok: false };
    }
  }, [user, loadMyBestPicks, loadSharedPlaces, sharedCategory]);


  const handleBestPickRemove = useCallback(async (pickId) => {
    if (!user) return;
    try {
      await axios.delete(`${API_BASE}/shared-map/my-picks/${pickId}`);
      await Promise.all([loadMyBestPicks(), loadSharedPlaces(sharedCategory)]);
      setToast("Removed from best picks");
    } catch (e) { setToast("Remove failed"); }
  }, [user, loadMyBestPicks, loadSharedPlaces, sharedCategory]);

  const handleSharedMarkerClick = useCallback((place) => {
    setSelectedRestaurant({ ...place, sources: [], isPersonal: false, isShared: true });
  }, []);

  const [modeTransition, setModeTransition] = useState(null); // null | "in" | "out"
  const modeTarget = useRef(null);
  const handleMapModeChange = (mode) => {
    if (mode === mapMode || modeTransition) return;
    modeTarget.current = mode;
    setModeTransition("in");
    setSelectedRestaurant(null);
    if (mode === "shared") { setShowPersonal(false); setSelectedFollowingIds([]); }
    if (mode === "personal") { setShowPersonal(true); }
    setTimeout(() => {
      setMapMode(mode);
      localStorage.setItem("mapMode", mode);
      setModeTransition("out");
      setTimeout(() => setModeTransition(null), 1200);
    }, 900);
  };

  // Combine server places + guest places for display
  const allPlaces = user ? personalPlaces : [...personalPlaces, ...guestPlaces];
  const filteredPersonalPlaces = activeFilter ? allPlaces.filter((p) => p.status === activeFilter) : allPlaces;
  const filteredFollowingPlaces = activeFilter
    ? followingPlaces.map((fp) => ({ ...fp, places: fp.places.filter((p) => p.status === activeFilter) }))
    : followingPlaces;
  // In shared mode, exclude places that overlap with best picks
  const personalForMap = mapMode === "shared"
    ? filteredPersonalPlaces.filter((p) => {
        const allBestIds = Object.values(myBestPicks).flat().map((b) => b.personal_place_id);
        return !allBestIds.includes(p.id);
      })
    : filteredPersonalPlaces;
  const visibleRestaurants = restaurants.filter((r) => !hiddenIds.has(r.id));

  // ── Loading ──────────────────────────────────────────────────
  if (loading) return (
    <div style={{
      position: "fixed", inset: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "#faf9f6", flexDirection: "column", gap: 10,
    }}>
      <h1 style={{ fontFamily: FH, fontStyle: "italic", fontSize: 26, color: "#655d54", margin: 0, letterSpacing: "-0.02em" }}>
        OurCuration
      </h1>
      <p style={{ fontFamily: FL, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.25em", color: "#c7c4bf", margin: 0 }}>
        {t("app.tagline")}
      </p>
    </div>
  );

  // Public routes — accessible without login
  const profileMatch = window.location.pathname.match(/^\/@(.+)$/);
  if (profileMatch) {
    const pNick = decodeURIComponent(profileMatch[1]);
    return <PublicProfile nickname={pNick} />;
  }
  const listMatch = window.location.pathname.match(/^\/list\/(\d+)$/);
  if (listMatch) {
    return <PublicListPage listId={parseInt(listMatch[1])} />;
  }
  const bestMatch = window.location.pathname.match(/^\/best\/(.+)$/);
  if (bestMatch) {
    return <PublicBestPicksPage nickname={decodeURIComponent(bestMatch[1])} />;
  }
  const tripMatch = window.location.pathname.match(/^\/trip\/(\d+)$/);
  if (tripMatch) {
    return <PublicTripPage tripId={parseInt(tripMatch[1])} />;
  }

  // Check auth requirement on tab switch — only profile requires login
  const requireAuth = (tab) => {
    if (!user && tab === "profile") {
      setShowLoginPrompt(true);
      return true;
    }
    return false;
  };

  // ── Mobile pull-to-refresh (non-map tabs) ───────────────────
  const handlePullStart = (e) => {
    if (!isMobile) return;
    const el = e.currentTarget;
    if (el && el.scrollTop <= 0) pullStartY.current = e.touches[0].clientY;
  };
  const handlePullEnd = (e) => {
    if (pullStartY.current === null) return;
    const diff = e.changedTouches[0].clientY - pullStartY.current;
    pullStartY.current = null;
    if (diff > 80 && !pullRefreshing) {
      setPullRefreshing(true);
      handleRefresh().finally(() => setPullRefreshing(false));
    }
  };

  const handleTabChange = (tab) => {
    if (requireAuth(tab)) return;
    // Clear preview when switching tabs
    if (previewPlace) { setPreviewPlace(null); if (previewMarkerRef.current) { previewMarkerRef.current.map = null; previewMarkerRef.current = null; } }
    // Desktop: clicking same tab toggles it off; map tab closes panel
    if (!isMobile && (tab === activeTab || tab === "map")) {
      setActiveTab("map");
      return;
    }
    setActiveTab(tab);
  };

  const renderPanel = (tab) => {
    if (tab === "curation")      return <CurationTab personalPlaces={allPlaces} onPlaceAdded={addPlace} onDataChange={loadPersonalPlaces} onGuestSave={addGuestPlace} tutorialStep={tutorialStep} onTutorialAdvance={(nextStep) => setTutorialStep(nextStep)} onViewMap={() => setActiveTab("map")} onViewTrip={(tripData) => {
      prevTabBeforeTrip.current = activeTab;
      setActiveTrip(tripData);
      setActiveTab("map");
      // Save trip to backend (upload photos first) — only for logged-in users
      if (user && !tripData.id && tripData.stops?.length > 0) {
        (async () => {
          const stopsPayload = [];
          for (let i = 0; i < tripData.stops.length; i++) {
            const s = tripData.stops[i];
            let photoUrls = [];
            const blobPhotos = (s.photos || []).filter((p) => p.blobUrl || p.file);
            if (blobPhotos.length > 0) {
              try {
                const batch = blobPhotos.slice(0, 5);
                const formData = new FormData();
                for (const p of batch) {
                  if (p.file) {
                    formData.append("files", p.file);
                  } else if (p.blobUrl) {
                    const resp = await fetch(p.blobUrl);
                    const blob = await resp.blob();
                    formData.append("files", blob, `photo_${i}.jpg`);
                  }
                }
                const res = await axios.post(`${API_BASE}/upload/photos`, formData);
                photoUrls = res.data.urls || [];
              } catch (err) { console.error("Photo upload failed:", err); }
            }
            stopsPayload.push({
              place_id: s.place_id || null,
              order: i,
              visited_at: s.visited_at || null,
              name: s.name || null,
              lat: s.lat,
              lng: s.lng,
              photo_urls: photoUrls.length > 0 ? photoUrls : null,
              memo: s.memo || null,
            });
          }
          const payload = {
            name: tripData.name,
            date: tripData.date || null,
            is_public: tripData.is_public != null ? tripData.is_public : true,
            stops: stopsPayload,
          };
          axios.post(`${API_BASE}/trips/`, payload)
            .then((res) => { console.log("Trip saved:", res.data.id); setActiveTrip((prev) => ({ ...prev, id: res.data.id })); })
            .catch((err) => console.error("Trip save failed:", err));
        })();
      }
    }} onViewOnMap={(place) => {
      if (previewMarkerRef.current) { previewMarkerRef.current.map = null; previewMarkerRef.current = null; }
      setPreviewPlace(place);
      setActiveTab("map");
      if (mapMode !== "personal") { setMapMode("personal"); localStorage.setItem("mapMode", "personal"); }
      setShowPersonal(true);
      setTimeout(() => {
        centerMapOnPlace(place.lat, place.lng, { noOffset: true });
        if (mapRef.current && window.google?.maps?.marker?.AdvancedMarkerElement) {
          const dot = document.createElement("div");
          dot.innerHTML = `<div style="width:22px;height:22px;border-radius:50%;background:#4285F4;border:2.5px solid #fff;box-shadow:0 2px 10px rgba(66,133,244,0.5);"></div>`;
          previewMarkerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
            position: { lat: place.lat, lng: place.lng },
            map: mapRef.current,
            content: dot,
            zIndex: 999,
          });
        }
      }, 100);
    }} myBestPicks={myBestPicks} onBestPickAdd={handleBestPickAdd} onBestPickRemove={handleBestPickRemove} onRefresh={() => { loadPersonalPlaces(); loadMyBestPicks(); loadFolders(); }} pushNav={pushNav} onPlaceClick={(p) => {
      pushNav({ activeTab: "curation", selectedRestaurant: null });
      setSelectedRestaurant({ id: p.id, name: p.name, lat: p.lat, lng: p.lng, status: p.status, user_id: p.user_id, isPersonal: true, sources: [] });
      setActiveTab("map");
      centerMapOnPlace(p.lat, p.lng);
    }} />;
    if (tab === "social")        return <SocialTab
      personalPlaces={allPlaces}
      onPlaceClick={(p) => {
        setSelectedRestaurant({ id: p.id, name: p.name, lat: p.lat, lng: p.lng, status: p.status, isPersonal: true, sources: [] });
        setActiveTab("map");
        centerMapOnPlace(p.lat, p.lng);
      }}
      onDataChange={loadPersonalPlaces}
      onNavigate={(tab) => setActiveTab(tab)}
      onUnreadChange={setUnreadCount}
      onNotificationPlaceClick={(p) => {
        setSelectedRestaurant({ id: p.id, name: p.name, lat: p.lat, lng: p.lng, isPersonal: true, sources: [] });
        setActiveTab("map");
        centerMapOnPlace(p.lat, p.lng);
      }}
      onViewUserProfile={handleViewUserProfile}
      onViewTrip={async (tripId) => {
        try {
          const res = await axios.get(`${API_BASE}/trips/${tripId}`);
          const trip = res.data;
          const tripData = {
            id: trip.id,
            name: trip.name,
            date: trip.date,
            stops: trip.stops.map((s) => ({
              lat: s.lat, lng: s.lng,
              name: s.name || "",
              time: s.visited_at || "",
              memo: s.memo || "",
              thumbnailUrl: s.photo_url || (s.photo_urls?.[0]) || null,
              photos: (s.photo_urls || []).map((url) => ({ url })),
            })),
          };
          setActiveTrip(tripData);
          setActiveTab("map");
        } catch {}
      }}
    />;
    if (tab === "profile")       return <ProfilePage personalPlaces={personalPlaces} onViewMap={() => setActiveTab("map")} onViewUserProfile={handleViewUserProfile} myBestPicks={myBestPicks} onBestPickAdd={handleBestPickAdd} onBestPickRemove={handleBestPickRemove} onRefresh={() => { loadPersonalPlaces(); loadFollowingList(); loadMyBestPicks(); loadFolders(); loadUnread(); }} pushNav={pushNav} onStartTutorial={() => { setActiveTab("map"); setTimeout(() => setTutorialStep(0), 300); }} onPlaceClick={(p) => {
      pushNav({ activeTab: "profile", selectedRestaurant: null });
      setSelectedRestaurant({ id: p.id, name: p.name, lat: p.lat, lng: p.lng, status: p.status, user_id: p.user_id, isPersonal: true, sources: [] });
      setActiveTab("map");
      centerMapOnPlace(p.lat, p.lng);
    }} />;
    return null;
  };

  return (
    <div className="app">
      {/* Landscape block overlay — mobile only */}
      <div className="landscape-block" style={{
        display: "none",
        position: "fixed", inset: 0, zIndex: 99999,
        background: "#faf9f6",
        flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 40, color: "#655d54" }}>screen_lock_rotation</span>
        <p style={{ fontFamily: FL, fontSize: 15, fontWeight: 600, color: "#655d54", margin: 0 }}>Please rotate your device</p>
        <p style={{ fontFamily: FL, fontSize: 12, color: "#8a8e8a", margin: 0 }}>This app works best in portrait mode</p>
      </div>
      <InteractiveTutorial
        step={tutorialStep}
        onSkip={() => { localStorage.setItem("onboarding_dismissed", "1"); setTutorialStep(null); }}
        onBack={() => {
          if (tutorialStep == null || tutorialStep <= 0) return;
          let prev = tutorialStep - 1;
          // Skip hidden step 4 (SavePlaceModal wait)
          if (prev === 4) prev = 3;
          // Restore correct tab for previous step
          if (prev <= 0) setActiveTab("map");
          else if (prev >= 1 && prev <= 7) { if (activeTab !== "curation") setActiveTab("curation"); }
          else if (prev === 8 || prev === 9) { if (activeTab !== "map") setActiveTab("map"); }
          setTutorialStep(prev);
        }}
        onNext={() => {
          if (tutorialStep != null && tutorialStep < 11) setTutorialStep(tutorialStep + 1);
          else { localStorage.setItem("onboarding_dismissed", "1"); setTutorialStep(null); }
        }}
      />

      {/* Guest banner removed — sign-in button is next to map controls */}

      {showLoginPrompt && !user && (
        <LoginPrompt onClose={() => { setShowLoginPrompt(false); setActiveTab("map"); }} />
      )}

      {/* ─── Mobile ──────────────────────────────────────────── */}
      {isMobile && (
        <>
          {/* Map always rendered (behind) */}
          <div style={{ position: "fixed", inset: 0, zIndex: 1 }}>
            <MapView
              restaurants={visibleRestaurants}
              personalPlaces={allPlaces}
              accounts={accounts}
              onMarkerClick={handleMarkerClick}
              onMapReady={(map) => {
                mapRef.current = map;
                if (pendingDeepLink.current) {
                  const p = pendingDeepLink.current;
                  pendingDeepLink.current = null;
                  centerMapOnPlace(p.lat, p.lng);
                }
              }}
              followingPlaces={[]}
              onFollowingMarkerClick={() => {}}
              folders={folders}
              focusPlace={selectedRestaurant}
              mapMode="personal"
              sharedPlaces={[]}
              onSharedMarkerClick={() => {}}
              activeTrip={activeTrip}
            />
            {/* Trip overlay banner */}
            {activeTrip && !tripPlayback && (
              <div style={{
                position: "absolute", top: "calc(8px + env(safe-area-inset-top, 0px))", left: 10, right: 10,
                zIndex: 20,
                display: "flex", alignItems: "center", gap: 8,
                background: "rgba(232,148,58,0.95)", color: "#fff",
                padding: "10px 12px", borderRadius: 12,
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                fontFamily: "'Manrope',-apple-system,sans-serif",
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20, flexShrink: 0 }}>trip_origin</span>
                <span style={{ fontSize: 15, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>{activeTrip.name}</span>
                <span style={{ fontSize: 12, opacity: 0.8, flexShrink: 0 }}>· {activeTrip.stops?.length}</span>
                {activeTrip.id && (
                  <button
                    onClick={handleShareTrip}
                    style={{
                      background: "rgba(255,255,255,0.25)", border: "none",
                      borderRadius: 8, padding: "8px 10px",
                      color: "#fff", cursor: "pointer",
                      display: "flex", alignItems: "center", flexShrink: 0,
                    }}
                  ><span className="material-symbols-outlined" style={{ fontSize: 20 }}>share</span></button>
                )}
                <button
                  onClick={() => setTripPlayback(true)}
                  style={{
                    background: "rgba(255,255,255,0.25)", border: "none",
                    borderRadius: 8, padding: "8px 10px",
                    color: "#fff", cursor: "pointer",
                    display: "flex", alignItems: "center", flexShrink: 0,
                  }}
                ><span className="material-symbols-outlined" style={{ fontSize: 20 }}>play_arrow</span></button>
                <button
                  onClick={() => { setActiveTrip(null); setTripPlayback(false); if (prevTabBeforeTrip.current) { setActiveTab(prevTabBeforeTrip.current); prevTabBeforeTrip.current = null; } }}
                  style={{
                    background: "rgba(255,255,255,0.25)", border: "none",
                    borderRadius: 8, padding: "8px 10px",
                    color: "#fff", fontSize: 14, fontWeight: 600,
                    cursor: "pointer", display: "flex", alignItems: "center", flexShrink: 0,
                  }}
                ><span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span></button>
              </div>
            )}
            {/* Trip playback */}
            {tripPlayback && activeTrip && (
              <TripPlayback
                trip={activeTrip}
                map={mapRef.current}
                onClose={() => setTripPlayback(false)}
                onSignIn={!user ? () => setShowLoginPrompt(true) : undefined}
              />
            )}
          </div>

          {/* Mobile map top — app name (hidden during trip) */}
          {showMap && !activeTrip && (
            <div style={{
              position: "fixed", top: 10, left: 0, right: 0, zIndex: 26,
              pointerEvents: "none",
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              <div style={{ padding: "0 14px", pointerEvents: "auto" }}>
                <div style={{
                  display: "inline-flex", alignItems: "center",
                  background: "rgba(250,249,246,0.92)", backdropFilter: "blur(8px)",
                  borderRadius: 999, padding: "6px 18px",
                  boxShadow: "0 2px 8px rgba(47,52,48,0.1)",
                }}>
                  <span style={{
                    fontFamily: FH, fontStyle: "italic", fontSize: 16,
                    fontWeight: 700, color: "#655d54",
                  }}>{t("app.name")}</span>
                </div>
              </div>
            </div>
          )}

          {/* Map overlay controls — bottom-right */}
          {showMap && !activeTrip && !(selectedRestaurant && window.innerWidth <= 768) && (
            <div style={{
              position: "fixed", right: 14, bottom: 80, zIndex: 31,
              display: "flex", flexDirection: "column",
              alignItems: "flex-end", gap: 8,
            }}>
              <LocationButton map={mapRef.current} />
            </div>
          )}

          {/* Guest sign-in button — visible on map, hidden during playback */}
          {showMap && !user && !tripPlayback && (
            <button
              onClick={() => setShowLoginPrompt(true)}
              style={{
                position: "fixed", right: 14,
                bottom: activeTrip ? 20 : 80,
                zIndex: 32,
                display: "flex", alignItems: "center", gap: 5,
                padding: "8px 14px", border: "none", borderRadius: 999,
                background: "rgba(101,93,84,0.92)", backdropFilter: "blur(8px)",
                color: "#fff6ef", cursor: "pointer",
                fontFamily: FL, fontSize: 11, fontWeight: 700,
                boxShadow: "0 2px 8px rgba(47,52,48,0.15)",
                whiteSpace: "nowrap",
                transition: "bottom 0.3s ease",
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>login</span>
              Sign in to save
            </button>
          )}

          {/* Mobile — place list bottom sheet */}
          {showMap && !selectedRestaurant && !activeTrip && window.innerWidth <= 768 && (
            <PersonalPlacesList
              personalPlaces={allPlaces}
              folders={folders}
              followingList={[]}
              selectedFollowingIds={[]}
              followingPlacesMap={{}}
              onPlaceSelect={(place) => {
                setSelectedRestaurant({
                  ...place, sources: [], isPersonal: true,
                  owner_nickname: place._ownerNickname || undefined,
                });
                centerMapOnPlace(place.lat, place.lng);
              }}
              onToggleFollowing={() => {}}
            />
          )}

          {/* Non-map tab content */}
          {!showMap && (
            <div
              onTouchStart={handlePullStart}
              onTouchEnd={handlePullEnd}
              style={{ position: "fixed", inset: 0, zIndex: 20, paddingBottom: "calc(64px + env(safe-area-inset-bottom, 0px))", overflowY: "auto", background: "#faf9f6" }}
            >
              {pullRefreshing && (
                <div style={{ textAlign: "center", padding: "10px 0", fontSize: 11, color: "#8a8e8a", fontFamily: FL }}>{t("map.refreshing")}</div>
              )}
              {renderPanel(activeTab)}
            </div>
          )}

          {/* Detail panel */}
          {selectedRestaurant && showMap && (
            <RestaurantPanel
              restaurant={selectedRestaurant}
              onClose={() => setSelectedRestaurant(null)}
              onGoBack={navDepth > 0 ? () => goBack() : undefined}
              canGoBack={navDepth > 0}
              onHide={hideRestaurant}
              sidebarWidth={0}
              onPlaceUpdated={handlePlaceUpdated} mapInstance={mapRef.current}
              onDataChange={loadPersonalPlaces}
              myBestPicks={myBestPicks}
              onBestPickAdd={handleBestPickAdd}
              
              onBestPickRemove={handleBestPickRemove}
              onViewUserProfile={handleViewUserProfile}
            />
          )}

          {activeTrip && !tripPlayback && (
            <TripTimeline
              trip={activeTrip}
              onSelectStop={(stop) => centerMapOnPlace(stop.lat, stop.lng, { tripMode: true })}
              onClose={() => { setActiveTrip(null); if (prevTabBeforeTrip.current) { setActiveTab(prevTabBeforeTrip.current); prevTabBeforeTrip.current = null; } }}
              bottomOffset={12}
            />
          )}
          {!tripPlayback && !activeTrip && <BottomTabBar activeTab={activeTab} onTabChange={handleTabChange} unreadCount={0} userNickname={user?.nickname} />}
        </>
      )}

      {/* ─── Desktop ─────────────────────────────────────────── */}
      {!isMobile && (
        <>
          {/* Sidebar (always visible) */}
          <Sidebar
            activeTab={activeTab} onTabChange={handleTabChange}
            personalPlaces={allPlaces}
            onDeletePersonalPlace={deletePersonalPlace}
            unreadCount={0}
            selectedFollowingIds={selectedFollowingIds}
            onToggleFollowing={handleToggleFollowing}
            followingList={followingList}
            onFollowChange={loadFollowingList}
            sidebarWidth={SIDEBAR_W}
            onViewUserProfile={handleViewUserProfile}
            onPlaceSelect={(place) => {
              setSelectedRestaurant({ ...place, sources: [], isPersonal: true });
              setActiveTab("map");
              centerMapOnPlace(place.lat, place.lng);
            }}
            mapMode={mapMode}
            onMapModeChange={handleMapModeChange}
            sharedCategory={sharedCategory}
            onSharedCategoryChange={setSharedCategory}
            sharedPlaces={sharedPlaces}
            onSharedPlaceSelect={(place) => {
              handleSharedMarkerClick(place);
              setActiveTab("map");
              centerMapOnPlace(place.lat, place.lng);
            }}
          />

          {/* Map — always rendered full screen behind */}
          <div style={{
            position: "fixed",
            left: SIDEBAR_W, right: 0, top: 0, bottom: 0,
            zIndex: 1,
          }}>
            <MapView
              restaurants={visibleRestaurants}
              personalPlaces={allPlaces}
              accounts={accounts}
              onMarkerClick={(id, isPersonal) => {
                handleMarkerClick(id, isPersonal);
                setActiveTab("map");
              }}
              onMapReady={(map) => {
                mapRef.current = map;
                if (pendingDeepLink.current) {
                  const p = pendingDeepLink.current;
                  pendingDeepLink.current = null;
                  centerMapOnPlace(p.lat, p.lng);
                }
              }}
              followingPlaces={[]}
              onFollowingMarkerClick={() => {}}
              folders={folders}
              focusPlace={selectedRestaurant}
              mapMode="personal"
              sharedPlaces={[]}
              onSharedMarkerClick={() => {}}
              activeTrip={activeTrip}
            />
            {/* Trip overlay banner (desktop) */}
            {activeTrip && !tripPlayback && (
              <div style={{
                position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)",
                zIndex: 20,
                display: "flex", alignItems: "center", gap: 8,
                background: "rgba(232,148,58,0.95)", color: "#fff",
                padding: "8px 14px 8px 12px", borderRadius: 10,
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                fontFamily: "'Manrope',-apple-system,sans-serif",
                maxWidth: 500, whiteSpace: "nowrap",
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, flexShrink: 0 }}>trip_origin</span>
                <span style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{activeTrip.name}</span>
                <span style={{ fontSize: 11, opacity: 0.8, flexShrink: 0 }}>· {activeTrip.stops?.length} stops</span>
                {activeTrip.id && (
                  <button
                    onClick={handleShareTrip}
                    style={{
                      background: "rgba(255,255,255,0.25)", border: "none",
                      borderRadius: 6, padding: "2px 8px",
                      color: "#fff", fontSize: 14, fontWeight: 600,
                      cursor: "pointer", marginLeft: 4,
                      display: "flex", alignItems: "center",
                    }}
                  ><span className="material-symbols-outlined" style={{ fontSize: 16 }}>share</span></button>
                )}
                <button
                  onClick={() => setTripPlayback(true)}
                  style={{
                    background: "rgba(255,255,255,0.25)", border: "none",
                    borderRadius: 6, padding: "2px 8px",
                    color: "#fff", fontSize: 14, fontWeight: 600,
                    cursor: "pointer",
                    display: "flex", alignItems: "center",
                  }}
                ><span className="material-symbols-outlined" style={{ fontSize: 16 }}>play_arrow</span></button>
                <button
                  onClick={() => { setActiveTrip(null); setTripPlayback(false); if (prevTabBeforeTrip.current) { setActiveTab(prevTabBeforeTrip.current); prevTabBeforeTrip.current = null; } }}
                  style={{
                    background: "rgba(255,255,255,0.25)", border: "none",
                    borderRadius: 6, padding: "2px 8px",
                    color: "#fff", fontSize: 12, fontWeight: 600,
                    cursor: "pointer",
                  }}
                >✕</button>
              </div>
            )}
            {/* Trip playback (desktop) */}
            {tripPlayback && activeTrip && (
              <TripPlayback
                trip={activeTrip}
                map={mapRef.current}
                onClose={() => setTripPlayback(false)}
                leftOffset={SIDEBAR_W}
                onSignIn={!user ? () => setShowLoginPrompt(true) : undefined}
              />
            )}
            {/* Trip timeline (desktop) */}
            {activeTrip && !tripPlayback && (
              <TripTimeline
                trip={activeTrip}
                onSelectStop={(stop) => centerMapOnPlace(stop.lat, stop.lng, { noOffset: true })}
                onClose={() => { setActiveTrip(null); if (prevTabBeforeTrip.current) { setActiveTab(prevTabBeforeTrip.current); prevTabBeforeTrip.current = null; } }}
                bottomOffset={20}
                leftOffset={SIDEBAR_W}
              />
            )}
            {/* Map controls — bottom-right */}
            <div style={{
              position: "absolute",
              right: 14, bottom: 14,
              display: tripPlayback ? "none" : "flex", flexDirection: "column",
              alignItems: "flex-end", gap: 8,
              zIndex: 26,
            }}>
              <LocationButton map={mapRef.current} />
            </div>
            {!user && !tripPlayback && (
              <button
                onClick={() => setShowLoginPrompt(true)}
                style={{
                  position: "absolute", right: 14, bottom: 14, zIndex: 32,
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "8px 14px", border: "none", borderRadius: 999,
                  background: "rgba(101,93,84,0.92)", backdropFilter: "blur(8px)",
                  color: "#fff6ef", cursor: "pointer",
                  fontFamily: FL, fontSize: 11, fontWeight: 700,
                  boxShadow: "0 2px 8px rgba(47,52,48,0.15)",
                  whiteSpace: "nowrap",
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>login</span>
                Sign in to save
              </button>
            )}
          </div>

          {/* Slide panel — non-map tabs */}
          {!showMap && (
            <div style={{
              position: "fixed",
              left: SIDEBAR_W, top: 0,
              width: PANEL_W, height: "100vh",
              background: "#faf9f6",
              zIndex: 30,
              boxShadow: "4px 0 32px rgba(47,52,48,0.10)",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              animation: "slideInPanel 0.22s cubic-bezier(0.16,1,0.3,1)",
            }}>
              {renderPanel(activeTab)}
            </div>
          )}

          {/* Detail panel — on marker click in map tab */}
          {selectedRestaurant && showMap && (
            <RestaurantPanel
              restaurant={selectedRestaurant}
              onClose={() => setSelectedRestaurant(null)}
              onGoBack={navDepth > 0 ? () => goBack() : undefined}
              canGoBack={navDepth > 0}
              onHide={hideRestaurant}
              sidebarWidth={SIDEBAR_W}
              onPlaceUpdated={handlePlaceUpdated} mapInstance={mapRef.current}
              onDataChange={loadPersonalPlaces}
              myBestPicks={myBestPicks}
              onBestPickAdd={handleBestPickAdd}
              
              onBestPickRemove={handleBestPickRemove}
              onViewUserProfile={handleViewUserProfile}
            />
          )}
        </>
      )}

      {/* Other user profile — desktop: side panel / mobile: full screen */}
      {viewingUserNickname && (
        isMobile ? (
          <UserProfileView
            nickname={viewingUserNickname}
            onClose={() => { if (!goBack()) setViewingUserNickname(null); }}
            canGoBack={navDepth > 0}
            onViewMap={handleViewUserMap}
            onPlaceClick={(place) => {
              setSelectedRestaurant({ ...place, sources: [], isPersonal: true, owner_nickname: viewingUserNickname });
              setActiveTab("map");
              if (place.lat && place.lng) centerMapOnPlace(place.lat, place.lng);
            }}
          />
        ) : (
          <div style={{
            position: "fixed",
            left: SIDEBAR_W, top: 0,
            width: PANEL_W, height: "100vh",
            background: "#faf9f6",
            zIndex: 35,
            boxShadow: "4px 0 32px rgba(47,52,48,0.10)",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            animation: "slideInPanel 0.22s cubic-bezier(0.16,1,0.3,1)",
          }}>
            <UserProfileView
              nickname={viewingUserNickname}
              onClose={() => { if (!goBack()) setViewingUserNickname(null); }}
              canGoBack={navDepth > 0}
              onViewMap={handleViewUserMap}
              onPlaceClick={(place) => {
                setSelectedRestaurant({ ...place, sources: [], isPersonal: true, owner_nickname: viewingUserNickname });
                setActiveTab("map");
                if (place.lat && place.lng) centerMapOnPlace(place.lat, place.lng);
              }}
              embedded
            />
          </div>
        )
      )}

      {/* Preview card — from search View on Map (shared between mobile & desktop) */}
      {previewPlace && showMap && !selectedRestaurant && (() => {
        const alreadySaved = allPlaces.some((p) =>
          (previewPlace.google_place_id && p.google_place_id === previewPlace.google_place_id) ||
          (!previewPlace.google_place_id && p.name === previewPlace.name && p.address === previewPlace.address)
        );
        return (
          <div style={{
            position: "fixed",
            left: isMobile ? 0 : SIDEBAR_W,
            right: 0,
            bottom: isMobile ? "calc(64px + env(safe-area-inset-bottom, 0px))" : 24,
            zIndex: 51, padding: isMobile ? "0 14px 14px" : "0 24px 0",
            maxWidth: isMobile ? "none" : 420,
            animation: "slideUp 0.25s cubic-bezier(0.16,1,0.3,1)",
          }}>
            <div style={{
              background: "#fff", borderRadius: 16,
              boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
              padding: "16px 18px", display: "flex", flexDirection: "column", gap: 8,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontFamily: FH, fontSize: 16, fontWeight: 700, color: "#2f3430" }}>{previewPlace.name}</p>
                  {previewPlace.address && (
                    <p style={{ margin: "4px 0 0", fontFamily: FL, fontSize: 11, color: "#8a8e8a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{previewPlace.address}</p>
                  )}
                  {previewPlace.category && (
                    <p style={{ margin: "3px 0 0", fontFamily: FL, fontSize: 10, color: "#a8a29e", textTransform: "capitalize" }}>{previewPlace.category}</p>
                  )}
                </div>
                <button onClick={() => {
                  setPreviewPlace(null);
                  if (previewMarkerRef.current) { previewMarkerRef.current.map = null; previewMarkerRef.current = null; }
                }} style={{
                  background: "none", border: "none", cursor: "pointer", padding: 4, flexShrink: 0,
                  color: "#8a8e8a",
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
                </button>
              </div>
              {alreadySaved ? (
                <div style={{
                  width: "100%", padding: "14px", background: "#edeeea", color: "#655d54",
                  borderRadius: 10, fontFamily: FL, fontSize: 14, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check_circle</span>
                  {t("search.saved")}
                </div>
              ) : (
                <button onClick={() => {
                  setPreviewSaveOpen(true);
                }} style={{
                  width: "100%", padding: "14px", background: "#655d54", color: "#fff6ef",
                  border: "none", borderRadius: 10, fontFamily: FL, fontSize: 14, fontWeight: 700,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  WebkitTapHighlightColor: "transparent", minHeight: 44,
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
                  {t("search.add_to_my_places")}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* SavePlaceModal from preview */}
      {previewSaveOpen && previewPlace && (
        <SavePlaceModal
          isGuest={!user}
          place={{
            name: previewPlace.name,
            address: previewPlace.address || "",
            lat: previewPlace.lat,
            lng: previewPlace.lng,
            google_place_id: previewPlace.google_place_id || "",
            category: previewPlace.category || null,
          }}
          onSave={async (data) => {
            try {
              if (user) {
                const res = await axios.post(`${API_BASE}/personal-places/`, data);
                addPlace(res.data);
              } else {
                const gp = addGuestPlace(data);
                addPlace(gp);
              }
              setPreviewSaveOpen(false);
              setPreviewPlace(null);
              if (previewMarkerRef.current) { previewMarkerRef.current.map = null; previewMarkerRef.current = null; }
            } catch (e) { setToast(e.response?.data?.detail || "Failed to save"); throw e; }
          }}
          onClose={() => setPreviewSaveOpen(false)}
        />
      )}

      <Toast message={toast} onClose={() => setToast(null)} />

      <style>{`
        @keyframes slideInPanel {
          from { opacity: 0; transform: translateX(-16px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
